/**
 * Mission Run Results Slice
 *
 * GW-8027 (Phase 3/4): Zustand slice that stores a single run's poll-contract
 * `Run` record plus its per-product payloads (Entity Graph, Evidence
 * Register, Audit Trail, Cost Ledger, Event Timeline, Due Diligence Report),
 * keyed by `runResultsKey(missionId, runId)` — the run-results page family
 * renders from this slice.
 *
 * All fetches go through the live path: `missionsDirectApi.getRun` for the
 * Run record, and the existing `src/api/missions/*` mappers for product
 * bodies. GW-9862 removed this slice's former all-zeros fixture-mode branch
 * (`FIXTURE_MISSION_ID`/`FIXTURE_RUN_ID`), which served
 * `src/mocks/missions/run-results` demo data instead of hitting the API.
 *
 * GW-9864: PROD-027 (Subject Profile) is now backed by a real live GET
 * (`missionsDirectApi.getRunProduct` ->
 * `GET /v1/missions/{missionId}/runs/{runId}/products/{productId}`, per
 * `handler/products.go`'s `GetProduct`), mapped via
 * `mapRunProductToSubjectProfileAssembly`. A 404 (the product has not been
 * assembled for this run yet — a real, expected response, not an error to
 * retry indefinitely) still falls back to the honest-empty
 * `emptySubjectProfile()`; every other failure propagates so
 * `productStatusByRunKey` genuinely reflects `'error'`.
 *
 * GW-10693: PROD-CORE-003 (Audit Trail) and PROD-CORE-004 (Cost Ledger) now
 * also trust a real 200 response body — see `resolveProductPayload`. Before
 * this fix, `fetchProduct`/`fetchAllRunProducts` unconditionally discarded
 * every non-PROD-027 response and substituted `emptyProduct(productId)`,
 * on the theory that gw-missions-api's old handler coerced every product
 * through one Go struct hard-typed to `SubjectProfileElement` sections,
 * so PROD-CORE-003/004's real content could never survive that shape. Once
 * gw-missions-api (GW-10691) returns each product's raw JSON body verbatim
 * instead, PROD-CORE-003/004's real bodies match gw-ui's
 * `AuditTrailAssembly`/`CostLedgerProduct` types field-for-field and must be
 * stored/rendered, not discarded — `isAuditTrailAssembly`/
 * `isCostLedgerProduct` guard against a genuinely malformed or
 * still-old-shaped response falling back to honest-empty instead of being
 * trusted as real content.
 *
 * PROD-CORE-001 (Entity Graph), PROD-CORE-002 (Evidence Register), and
 * PROD-CORE-005 (Event Timeline) now trust a real 200 body the same way, via
 * `isEntityGraphData`/`isEvidenceRegisterProduct`/`isEventTimelineProduct` —
 * gw-ui's `EntityGraphData`/`EvidenceRegisterProduct`/`EventTimelineProduct`
 * types already existed; the prior "no confirmed target shape yet" fallback
 * was stale. PROD-CORE-007 (Findings Log) has no confirmed payload shape
 * anywhere yet and stays on the honest-empty fallback until one exists.
 * PROD-CORE-006 (Mission Metadata) remains out of scope for this slice
 * entirely — it is not a member of `RunResultsProductId` and has no
 * dedicated product page/route; per `RunResultsPage.tsx`'s own header
 * comment and the target-IA design docs, Mission Metadata folds into
 * `RunResultsPage`'s header instead.
 *
 * GW-8987 (flicker fix, stale-while-revalidate): `RunDetailPage` polls
 * `fetchRunResults` every 5s while a run is non-terminal. Previously this
 * function set `runStatusByRunKey[key] = 'loading'` unconditionally on
 * every call, including background refresh ticks after the initial run was
 * already cached — `RunDetailPage` derived `isLoading` from that status and
 * branched its render on it, so the whole `MissionRunPage` tree unmounted
 * into a full-screen "Loading run..." panel on every poll tick. Fixed by
 * only setting `'loading'` when no run is cached yet for the key (captured
 * via `hasCachedRun`, read once before this call's first `set()`); a
 * background refresh instead keeps `runStatusByRunKey[key] === 'ready'` and
 * routes both success (clearing) and failure (a message) through the new
 * `runErrorByRunKey` map, so a failed background refresh is non-destructive:
 * the last-good `run` and `'ready'` status survive, and the caller can
 * surface `runErrorByRunKey[key]` as a banner instead of an unmount.
 *
 * GW-9655: dropped `batchByBatchId`/`batchStatusByBatchId`/`fetchBatchStatus`
 * (Subject Profile's former `GET /v1/batch/{batchId}` poll state). Composition
 * Plan v2 retired that endpoint (gw-datasets-api, GW-9576/GW-9579) along with
 * the `Run.batchId` field that keyed it (gw-missions-api, GW-9592), with no
 * replacement endpoint or field — see `RunDetailPage.tsx`'s header comment for
 * the full investigation. `run.status` (already fetched by `fetchRunResults`
 * above) is now the sole progress/swap signal RunDetailPage reads.
 */

import type { StateCreator } from 'zustand';

import { ApiError } from '@/api/apiError';
import { missionsDirectApi, runProductId } from '@/api/missionsDirectApiClient';
import { emptyCostLedgerProduct } from '@/api/missions/costLedger';
import { emptyEvidenceRegisterProduct } from '@/api/missions/evidenceRegister';
import { emptyEventTimelineProduct } from '@/api/missions/eventTimeline';
import { mapRunProductToSubjectProfileAssembly } from '@/api/missions/subjectProfileAdapter';

import type { RunProduct } from '@/api/missionsDirectApiClient';
import type { RunReportAssembly } from '@/types/runReport.types';
import type {
  AuditTrailAssembly,
  CostLedgerProduct,
  EntityGraphData,
  EvidenceRegisterProduct,
  EventTimelineProduct,
  Run,
} from '@gw/ui/types';

type StatusValue = 'idle' | 'loading' | 'ready' | 'error';

/** @public Stable contract name — the six run-results page agents (Entity
 * Graph, Evidence Register, Audit Trail, Cost Ledger, Event Timeline, Due
 * Diligence Report) import this directly even though only this module uses
 * it internally today. */
export type RunResultsProductId =
  | 'PROD-CORE-001'
  | 'PROD-CORE-002'
  | 'PROD-CORE-003'
  | 'PROD-CORE-004'
  | 'PROD-CORE-005'
  // Findings Log — no confirmed payload shape yet (see emptyFindingsLog);
  // tracked here so its card can still gate on productStatusByRunKey.
  // PROD-CORE-006 (Mission Metadata) is deliberately NOT a member — it
  // folds into RunResultsPage's header instead of getting its own card.
  | 'PROD-CORE-007'
  | 'PROD-006'
  // Subject Profile (OSINT-MISSION-29): a collection of SEC-051 cards, one per
  // subject. Same RunReportAssembly shape as PROD-006 — one section (SEC-051).
  | 'PROD-027';

/** @public Stable contract name — see `RunResultsProductId`. */
export interface RunResultsProductPayloadMap {
  'PROD-CORE-001': EntityGraphData;
  'PROD-CORE-002': EvidenceRegisterProduct;
  'PROD-CORE-003': AuditTrailAssembly;
  'PROD-CORE-004': CostLedgerProduct;
  'PROD-CORE-005': EventTimelineProduct;
  'PROD-CORE-007': RunReportAssembly;
  'PROD-006': RunReportAssembly;
  'PROD-027': RunReportAssembly;
}

export const runResultsKey = (missionId: string, runId: string): string => `${missionId}:${runId}`;

/** Honest empty PROD-006 assembly — no report content available yet. */
function emptyReport(): RunReportAssembly {
  return {
    productId: 'PROD-006',
    productName: 'Due Diligence Report',
    headline: '',
    assemblySummary: '0 of 15 sections assembled',
    sections: [],
  };
}

/** Honest empty PROD-027 (Subject Profile) assembly — no subjects profiled yet. */
function emptySubjectProfile(): RunReportAssembly {
  return {
    productId: 'PROD-027',
    productName: 'Subject Profile',
    headline: '',
    assemblySummary: '0 subjects profiled',
    sections: [],
  };
}

/** Honest empty PROD-CORE-001 assembly — no entity graph content available yet. */
function emptyEntityGraph(): EntityGraphData {
  return {
    nodes: [],
    edges: [],
    nodeCount: 0,
    edgeCount: 0,
    attributeCompletenessRate: 0,
    communityClusters: [],
    pendingNodeCount: 0,
    entityTypesPresent: [],
  };
}

/** Honest empty PROD-CORE-007 (Findings Log) assembly — no confirmed target
 * shape exists yet, so this reuses the generic RunReportAssembly envelope.
 * `productId` is cast past `RunReportAssembly`'s own `'PROD-006' | 'PROD-027'`
 * union — that union describes the two report-shaped products it was
 * originally modeled for, not every id borrowing its envelope shape. */
function emptyFindingsLog(): RunReportAssembly {
  return {
    productId: 'PROD-CORE-007',
    productName: 'Findings Log',
    headline: '',
    assemblySummary: '0 findings logged',
    sections: [],
  } as unknown as RunReportAssembly;
}

/** Honest empty PROD-CORE-003 assembly — no audit trail content available yet. */
function emptyAuditTrail(): AuditTrailAssembly {
  return {
    totalEventCount: 0,
    inferenceCallCount: 0,
    totalCostTokens: 0,
    runStartTime: '',
    runEndTime: '',
    terminationReason: '',
    merkleRoot: '',
    events: [],
  };
}

/**
 * Honest-empty fallback per product id, used whenever the real GET returns
 * no assembled content for this run (a 404, a 200 whose body cannot be
 * trusted as real content for this productId — see `resolveProductPayload`
 * — or, for PROD-CORE-001/002/005, always: no confirmed target shape exists
 * yet for those three, so every successful GET for them is treated as "no
 * content", not just an absent-object 404).
 */
function emptyProduct<K extends RunResultsProductId>(productId: K): RunResultsProductPayloadMap[K] {
  switch (productId) {
    case 'PROD-CORE-001':
      return emptyEntityGraph() as RunResultsProductPayloadMap[K];
    case 'PROD-CORE-002':
      return emptyEvidenceRegisterProduct() as RunResultsProductPayloadMap[K];
    case 'PROD-CORE-003':
      return emptyAuditTrail() as RunResultsProductPayloadMap[K];
    case 'PROD-CORE-004':
      return emptyCostLedgerProduct() as RunResultsProductPayloadMap[K];
    case 'PROD-CORE-005':
      return emptyEventTimelineProduct() as RunResultsProductPayloadMap[K];
    case 'PROD-CORE-007':
      return emptyFindingsLog() as RunResultsProductPayloadMap[K];
    case 'PROD-027':
      return emptySubjectProfile() as RunResultsProductPayloadMap[K];
    case 'PROD-006':
    default:
      return emptyReport() as RunResultsProductPayloadMap[K];
  }
}

/**
 * Runtime guard confirming a raw `RunProduct` GET response body is a real
 * PROD-CORE-003 (Audit Trail) assembly — gw-ui's `AuditTrailAssembly` shape,
 * field-for-field, per `gw-ui/src/types/audit-trail.types.ts` (the Python
 * producer's own docstring: "Mirrors gw-ui's AuditTrailAssembly interface
 * exactly"). Once gw-missions-api (GW-10691) passes the raw product JSON
 * through verbatim instead of coercing every product through one
 * SubjectProfileElement-typed struct, this is what a real 200 body for
 * PROD-CORE-003 looks like — checked structurally rather than trusted
 * blindly, so a genuinely malformed or still-old-shaped response correctly
 * falls back to honest-empty instead of rendering garbage.
 */
function isAuditTrailAssembly(body: unknown): body is AuditTrailAssembly {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as Record<string, unknown>;
  return (
    typeof b.totalEventCount === 'number' &&
    typeof b.merkleRoot === 'string' &&
    Array.isArray(b.events)
  );
}

/**
 * Runtime guard confirming a raw `RunProduct` GET response body is a real
 * PROD-CORE-004 (Cost Ledger) assembly — gw-ui's `CostLedgerProduct` shape,
 * field-for-field, per `gw-ui/src/types/cost-ledger.types.ts` (the Python
 * producer's own docstring: "Ported field-for-field from the frozen
 * consumer contract... ALL snake_case, no alias_generator"). See
 * `isAuditTrailAssembly`'s doc comment for why this is a structural check,
 * not a blind cast.
 */
function isCostLedgerProduct(body: unknown): body is CostLedgerProduct {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as Record<string, unknown>;
  return (
    b.product_id === 'PROD-CORE-004' &&
    typeof b.token_budget === 'number' &&
    Array.isArray(b.charge_items)
  );
}

/**
 * Runtime guard confirming a raw `RunProduct` GET response body is a real
 * PROD-CORE-001 (Entity Graph) assembly — gw-ui's `EntityGraphData` shape,
 * field-for-field, per `gw-ui/src/types/entity-graph.types.ts`. See
 * `isAuditTrailAssembly`'s doc comment for why this is a structural check,
 * not a blind cast.
 */
function isEntityGraphData(body: unknown): body is EntityGraphData {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as Record<string, unknown>;
  return typeof b.nodeCount === 'number' && typeof b.edgeCount === 'number';
}

/**
 * Runtime guard confirming a raw `RunProduct` GET response body is a real
 * PROD-CORE-002 (Evidence Register) assembly — gw-ui's
 * `EvidenceRegisterProduct` shape, field-for-field, per
 * `gw-ui/src/types/evidence-register.types.ts`. See `isAuditTrailAssembly`'s
 * doc comment for why this is a structural check, not a blind cast.
 */
function isEvidenceRegisterProduct(body: unknown): body is EvidenceRegisterProduct {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as Record<string, unknown>;
  return b.product_id === 'PROD-CORE-002' && typeof b.total_entry_count === 'number';
}

/**
 * Runtime guard confirming a raw `RunProduct` GET response body is a real
 * PROD-CORE-005 (Event Timeline) assembly — gw-ui's `EventTimelineProduct`
 * shape, field-for-field, per `gw-ui/src/types/event-timeline.types.ts`. See
 * `isAuditTrailAssembly`'s doc comment for why this is a structural check,
 * not a blind cast.
 */
function isEventTimelineProduct(body: unknown): body is EventTimelineProduct {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as Record<string, unknown>;
  return b.product_id === 'PROD-CORE-005' && typeof b.total_event_count === 'number';
}

/**
 * Resolves a real GET response body into this product's payload shape,
 * falling back to honest-empty only when the body cannot be trusted as
 * real content for `productId` (GW-10693). `RunProduct`'s typed fields
 * (`sections`/`meta`) describe the Subject Profile envelope specifically —
 * every PROD-CORE-* real body is a structurally different shape (Entity
 * Graph / Evidence Register / Audit Trail / Cost Ledger / Event Timeline),
 * so the raw, unnarrowed response is checked against each real target shape
 * rather than accessed through `RunProduct`. PROD-CORE-007 (Findings Log)
 * has no confirmed target shape anywhere yet and always resolves to
 * honest-empty.
 */
function resolveProductPayload<K extends RunResultsProductId>(
  productId: K,
  product: RunProduct
): RunResultsProductPayloadMap[K] {
  if (productId === 'PROD-027') {
    return mapRunProductToSubjectProfileAssembly(product) as RunResultsProductPayloadMap[K];
  }
  if (productId === 'PROD-CORE-001' && isEntityGraphData(product)) {
    return product as unknown as RunResultsProductPayloadMap[K];
  }
  if (productId === 'PROD-CORE-002' && isEvidenceRegisterProduct(product)) {
    return product as unknown as RunResultsProductPayloadMap[K];
  }
  if (productId === 'PROD-CORE-003' && isAuditTrailAssembly(product)) {
    return product as unknown as RunResultsProductPayloadMap[K];
  }
  if (productId === 'PROD-CORE-004' && isCostLedgerProduct(product)) {
    return product as unknown as RunResultsProductPayloadMap[K];
  }
  if (productId === 'PROD-CORE-005' && isEventTimelineProduct(product)) {
    return product as unknown as RunResultsProductPayloadMap[K];
  }
  // PROD-CORE-007: no confirmed target shape yet. Any id above on a
  // malformed/still-old-shaped body: never trust it — honest-empty.
  return emptyProduct(productId);
}

/**
 * GW-10160: PROD-027 and the PROD-CORE-* ids now go through the same real
 * live GET (`missionsDirectApi.getRunProduct` ->
 * `GET /v1/missions/{missionId}/runs/{runId}/products/{productId}`, per
 * `handler/products.go`'s `GetProduct`) — a 404 (not assembled yet) falls
 * back to the honest-empty payload. A real 200 is now trusted for
 * PROD-CORE-001/002/003/004/005 (see `resolveProductPayload`); PROD-CORE-007
 * still falls back to empty on every successful GET, not just on a 404 — no
 * confirmed target shape exists for it yet. PROD-006 is unchanged/out of
 * scope here — `gw-missions-api` has never implemented a Due Diligence
 * Report assembler, so it stays on the honest-empty fallback with no API
 * call, as before.
 */
async function fetchProduct<K extends RunResultsProductId>(
  productId: K,
  missionId: string,
  runId: string
): Promise<RunResultsProductPayloadMap[K]> {
  if (productId === 'PROD-006') {
    return emptyReport() as RunResultsProductPayloadMap[K];
  }
  try {
    const product = await missionsDirectApi.getRunProduct(missionId, runId, productId);
    return resolveProductPayload(productId, product);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      // Not assembled yet for this run — honest-empty, not an error.
      return emptyProduct(productId);
    }
    throw err;
  }
}

export interface MissionRunResultsSlice {
  runByRunKey: Record<string, Run | null>;
  runStatusByRunKey: Record<string, StatusValue>;
  /**
   * GW-8987: latest fetch-failure message for a run key, cleared on the next
   * successful fetch. Populated on EVERY failure (both the initial load and
   * background refreshes), but only the initial-load failure also downgrades
   * `runStatusByRunKey` to `'error'` — see `fetchRunResults`. `RunDetailPage`
   * reads this to drive `MissionRunPage`'s `error` banner prop without
   * unmounting the page when a cached run already exists.
   */
  runErrorByRunKey: Record<string, string | undefined>;
  productsByRunKey: Record<string, Partial<RunResultsProductPayloadMap>>;
  productStatusByRunKey: Record<string, Partial<Record<RunResultsProductId, StatusValue>>>;
  /**
   * Status of the `fetchAllRunProducts` list call itself, keyed by run —
   * distinct from `productStatusByRunKey`, which only ever gains entries for
   * product ids the response actually contained. Lets `RunResultsPage` show
   * a loading skeleton while this call is in flight instead of reading an
   * empty `productStatusByRunKey[key]` as "genuinely zero products" before
   * the fetch has even resolved.
   */
  runProductsListStatusByRunKey: Record<string, StatusValue>;
  fetchRunResults: (missionId: string, runId: string) => Promise<void>;
  fetchRunProduct: (
    missionId: string,
    runId: string,
    productId: RunResultsProductId
  ) => Promise<void>;
  /**
   * GW-10160: `RunResultsPage`'s real data source — one call to
   * `GET /v1/missions/{missionId}/runs/{runId}/products` (the real,
   * deployed `listRunProducts`) instead of fetching PROD-027 individually.
   * Populates `productsByRunKey`/`productStatusByRunKey` for every product
   * id the response actually contains, mapped the same way each id's
   * individual `fetchRunProduct` path does — see `resolveProductPayload`:
   * PROD-027 through the Subject Profile adapter; PROD-CORE-001/002/003/
   * 004/005 trusted as real content when their body matches gw-ui's
   * `EntityGraphData`/`EvidenceRegisterProduct`/`AuditTrailAssembly`/
   * `CostLedgerProduct`/`EventTimelineProduct` shape; every other id
   * (PROD-CORE-007, PROD-006) to its honest-empty payload. A product id
   * absent from the response is left unset (not yet assembled), same as an
   * individual 404 would leave it.
   * Sets `runProductsListStatusByRunKey[key]` to `'loading'` before the call
   * and `'ready'`/`'error'` after, so callers can distinguish "not fetched
   * yet" from "genuinely zero products".
   */
  fetchAllRunProducts: (missionId: string, runId: string) => Promise<void>;
}

export const createMissionRunResultsSlice: StateCreator<
  MissionRunResultsSlice,
  [],
  [],
  MissionRunResultsSlice
> = (set, get) => ({
  runByRunKey: {},
  runStatusByRunKey: {},
  runErrorByRunKey: {},
  productsByRunKey: {},
  productStatusByRunKey: {},
  runProductsListStatusByRunKey: {},

  fetchRunResults: async (missionId, runId) => {
    const key = runResultsKey(missionId, runId);
    // GW-8987 (flicker fix): a run already cached for this key means this
    // call is a background poll refresh, not the initial load — only the
    // initial load (no cached run yet) should show the full-screen loading
    // panel. Captured once, up front, before any `set()` in this call can
    // change it.
    const hasCachedRun = get().runByRunKey[key] != null;

    if (!hasCachedRun) {
      set({ runStatusByRunKey: { ...get().runStatusByRunKey, [key]: 'loading' } });
    }

    try {
      const run = await missionsDirectApi.getRun(missionId, runId);
      set({
        runByRunKey: { ...get().runByRunKey, [key]: run },
        runStatusByRunKey: { ...get().runStatusByRunKey, [key]: 'ready' },
        runErrorByRunKey: { ...get().runErrorByRunKey, [key]: undefined },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load run.';
      if (hasCachedRun) {
        // Background refresh failure: non-destructive — keep the last good
        // run and 'ready' status, surface the error as a banner instead of
        // unmounting the page into the full-screen error panel.
        set({ runErrorByRunKey: { ...get().runErrorByRunKey, [key]: message } });
      } else {
        set({
          runStatusByRunKey: { ...get().runStatusByRunKey, [key]: 'error' },
          runErrorByRunKey: { ...get().runErrorByRunKey, [key]: message },
        });
      }
      throw err;
    }
  },

  fetchRunProduct: async (missionId, runId, productId) => {
    const key = runResultsKey(missionId, runId);
    set({
      productStatusByRunKey: {
        ...get().productStatusByRunKey,
        [key]: { ...get().productStatusByRunKey[key], [productId]: 'loading' },
      },
    });

    try {
      const payload = await fetchProduct(productId, missionId, runId);
      set({
        productsByRunKey: {
          ...get().productsByRunKey,
          [key]: { ...get().productsByRunKey[key], [productId]: payload },
        },
        productStatusByRunKey: {
          ...get().productStatusByRunKey,
          [key]: { ...get().productStatusByRunKey[key], [productId]: 'ready' },
        },
      });
    } catch (err) {
      set({
        productStatusByRunKey: {
          ...get().productStatusByRunKey,
          [key]: { ...get().productStatusByRunKey[key], [productId]: 'error' },
        },
      });
      throw err;
    }
  },

  fetchAllRunProducts: async (missionId, runId) => {
    const key = runResultsKey(missionId, runId);
    set({
      runProductsListStatusByRunKey: { ...get().runProductsListStatusByRunKey, [key]: 'loading' },
    });

    try {
      const response = await missionsDirectApi.listRunProducts(missionId, runId);

      const payloads: Record<string, RunResultsProductPayloadMap[RunResultsProductId]> = {};
      const statuses: Partial<Record<RunResultsProductId, StatusValue>> = {};

      for (const product of response.products) {
        const productId = runProductId(product) as RunResultsProductId;
        payloads[productId] = resolveProductPayload(productId, product);
        statuses[productId] = 'ready';
      }

      set({
        productsByRunKey: {
          ...get().productsByRunKey,
          [key]: { ...get().productsByRunKey[key], ...payloads },
        },
        productStatusByRunKey: {
          ...get().productStatusByRunKey,
          [key]: { ...get().productStatusByRunKey[key], ...statuses },
        },
        runProductsListStatusByRunKey: {
          ...get().runProductsListStatusByRunKey,
          [key]: 'ready',
        },
      });
    } catch (err) {
      set({
        runProductsListStatusByRunKey: {
          ...get().runProductsListStatusByRunKey,
          [key]: 'error',
        },
      });
      throw err;
    }
  },
});
